import { EventTypes } from '../constants/event';

/**
 * WS-2 backpressure (HITL-1411).
 *
 * The audio WebSocket handler buffers incoming frames and flushes them on a
 * fixed 100ms interval. Under load a single slow flush lets the buffer grow
 * faster than it drains; each subsequent flush then has more to do, falls
 * further behind, and the loop spirals into event-loop starvation and an
 * eventual Chrome renderer crash (STATUS_BREAKPOINT / "Aw, Snap!").
 *
 * The release valve bounds the buffer so the loop sheds load instead of
 * spiraling. IMPORTANT: only AUDIO frames are ever dropped. Audio is the only
 * high-volume stream (thousands of frames vs. dozens of events) and stale audio
 * is useless, so most-recent-wins. Non-audio events (text, hypothesis, order,
 * check, connect/disconnect, session boundaries, …) are low-volume and losing
 * even one could corrupt the order or transcript, so they are NEVER dropped —
 * their content and relative order are always preserved.
 */

// Ceiling on the number of buffered AUDIO frames. Generous headroom: at a
// typical frame rate this is several seconds of audio, so it only trips under a
// genuine stall, never in normal operation. Non-audio events are not counted
// against or bounded by this.
export const MAX_BUFFERED_AUDIO_FRAMES = 500;

export const isAudioMessage = (message: any): boolean =>
  message?.event === EventTypes.audio;

/**
 * Bound the number of AUDIO frames in `buffer` in place, dropping the OLDEST
 * audio frames beyond `maxAudioFrames`. Non-audio events are never touched and
 * every message's relative order is preserved. Returns the number of audio
 * frames dropped (0 when within the limit).
 */
export const boundAudioFrames = (
  buffer: any[],
  maxAudioFrames: number = MAX_BUFFERED_AUDIO_FRAMES
): number => {
  let audioCount = 0;
  for (let i = 0; i < buffer.length; i++) {
    if (isAudioMessage(buffer[i])) audioCount++;
  }
  if (audioCount <= maxAudioFrames) return 0;

  const dropped = audioCount - maxAudioFrames;
  let toDrop = dropped;
  // Compact in place: skip the oldest `toDrop` audio frames, keep everything
  // else (all events + the most-recent audio) in its original order.
  let write = 0;
  for (let read = 0; read < buffer.length; read++) {
    const message = buffer[read];
    if (toDrop > 0 && isAudioMessage(message)) {
      toDrop--;
      continue;
    }
    buffer[write++] = message;
  }
  buffer.length = write;
  return dropped;
};
